import React, { useCallback, useState, FC, useMemo } from 'react';
import { Dropdown, Form, Checkbox, Button } from 'src/common';
import styles from './index.module.scss';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { faBell } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useForm } from 'antd/lib/form/Form';
import { useGetNotificationSettings, useUpdateNotificationSettings } from 'src/models/users/NotificationSettings/hooks';
import { NotificationGroupName } from 'src/models/notifications/types';
import { NotificationSettingMode, NotificationType } from '@tendium/prom-types/notifications';
import { useTranslation } from 'react-i18next';
import { trackNotificationsDropdown } from 'src/segment/events';

export interface IProps {
  id: string | undefined;
  type: NotificationType;
  groupName: NotificationGroupName;
  label: string;
  desc: string;
  eventSource: string;
}

export interface IFormState {
  [NotificationSettingMode.Email]: boolean;
}

export const NotificationsDropdown: FC<IProps> = props => {
  const { id, type, groupName, label, desc, eventSource } = props;
  const { t } = useTranslation();
  const { data: groups, loading } = useGetNotificationSettings();
  const [updateSettings] = useUpdateNotificationSettings();
  const [menuVisible, setMenuVisible] = useState(false);

  const onVisibleChange = useCallback(() => {
    setMenuVisible(prevState => !prevState);
  }, []);

  const [form] = useForm();

  const onValuesChange = useCallback(
    (changedValues: Partial<IFormState>, onOff: boolean) => {
      const value = changedValues[NotificationSettingMode.Email];
      if (value !== undefined && id) {
        updateSettings({
          name: Object.keys(NotificationType)[
            Object.values(NotificationType).indexOf(type)
          ] as keyof typeof NotificationType,
          mode: NotificationSettingMode.Email,
          value,
          itemId: id
        });
      }
      if (id) {
        trackNotificationsDropdown(onOff, eventSource, id);
      }
    },
    [id, eventSource, updateSettings, type]
  );

  const initialValues = useMemo(
    () => ({
      [NotificationSettingMode.Email]:
        (id &&
          groups
            .find(group => group.name === groupName)
            ?.notificationSettings.find(setting => setting.itemId === id)
            ?.notificationSettingValues.find(v => v.mode === NotificationSettingMode.Email)?.value) ||
        undefined
    }),
    [groups, id, groupName]
  );

  return (
    <div className={styles.container}>
      <Dropdown
        trigger={['click']}
        overlay={
          <div className={styles.overlay}>
            <h5 className={styles.title}>
              <span>{t('Notifications.notifications')}</span>
            </h5>
            <Form form={form} className={styles.form} onValuesChange={onValuesChange} initialValues={initialValues}>
              <Form.Item name={NotificationSettingMode.Email} valuePropName={'checked'}>
                <Checkbox className={styles.checkbox}>
                  <span className={styles.checkboxInner}>
                    <span className={styles.checkboxLabel}>{label}</span>
                    <span className={styles.checkboxDesc}>{desc}</span>
                  </span>
                </Checkbox>
              </Form.Item>
            </Form>
          </div>
        }
        onVisibleChange={onVisibleChange}
        visible={menuVisible}
        placement={'bottomRight'}
      >
        <Button
          className={styles.button}
          type={'text'}
          icon={<FontAwesomeIcon icon={faBell} className={styles.icon} />}
          loading={loading}
        >
          <span>{t('Notifications.notifications')}</span>
          <FontAwesomeIcon
            icon={faCaretDown}
            className={classNames(styles.toggleIcon, { [styles.isActive]: menuVisible })}
          />
        </Button>
      </Dropdown>
    </div>
  );
};

export default NotificationsDropdown;
